import { useCallback, useEffect, type Dispatch, type MutableRefObject, type SetStateAction } from "react";
import type { DockPanelKey } from "../components/dockPanelLabels";
import type { WaveformDisplayMode } from "../components/eegViewerDisplayHelpers";
import { getRowHeightModeFromChannelSpacing } from "../components/eegViewerDisplayHelpers";
import type { DualMontagePreference } from "../components/customization/customizationTypes";
import { normalizeHexColor } from "../types/eegViewerTraceTypes";
import {
  type DisplayProfileExtractors,
} from "../utils/eegViewerDisplayProfileConfigExtractors";
import { parseEegPaperTheme } from "../utils/eegWorkspaceTheme";
import { sanitizeChannelPositionOffsets } from "../utils/displayProfileVisualState";
import {
  RENDER_THEME_MAP,
  type DisplayProfileBackgroundTheme,
  type DisplayProfileConfig,
  type DisplayProfileGridDensity,
  type DisplayProfilePolicyResponse,
  type EEGDisplayProfile,
} from "../types/eegViewerDisplayProfileTypes";

export type { DisplayProfileExtractors };

export function withAlphaHex(baseColor: string, alphaHex: string): string {
  if (!baseColor.startsWith("#") || baseColor.length !== 7) return baseColor;
  return `${baseColor}${alphaHex}`;
}

export type ViewerConfiguration = {
  config_id?: string;
  config_name?: string;
  montage_id: string;
  sensitivity_value: number;
  time_scale: number;
  high_pass_filter: number;
  low_pass_filter: number;
  notch_filter_60hz: boolean;
  notch_filter_50hz?: boolean;
  remove_dc_offset?: boolean;
  polarity?: "negative-up" | "positive-up";
  channel_color_mode?: "uniform" | "alternating" | "by-type";
  reference_type: string;
  playback_speed: number;
  is_default?: boolean;
};

type RendererTickHandle = { updateConfig: (config: Record<string, unknown>) => void };

export function useEEGDisplayProfileSession(params: {
  displayProfilePolicy: DisplayProfilePolicyResponse;
  selectedDisplayProfile: EEGDisplayProfile | null;
  currentConfig: ViewerConfiguration | null;
  profileTheme: DisplayProfileBackgroundTheme;
  profileGridDensity: DisplayProfileGridDensity;
  profileChannelColors: Record<string, string>;
  profileWaveformThickness: number;
  profileChannelSpacing: number;
  profileLabelFontSize: number;
  profileChannelPositionOffsets: Record<string, number>;
  waveformDisplayMode: WaveformDisplayMode;
  allowTraceOverflow: boolean;
  refitOnChannelChange: boolean;
  selectedTrendProfileId: string;
  dualMontagePreference: DualMontagePreference;
  dockBottomHeightPx: number;
  dockRightWidthPx: number;
  annotationPanelWidthPx: number;
  dockBottomCollapsed: boolean;
  dockRightCollapsed: boolean;
  dockBottomActiveTab: DockPanelKey | null;
  dockPanelVisibility: Record<DockPanelKey, boolean>;
  dockPanelOrder: DockPanelKey[];
  crosshairEnabled: boolean;
  showGrid: boolean;
  showChannelGroupLabels: boolean;
  showScaleMarkers: boolean;
  hiddenChannels: Set<string>;
  buildDisplayProfileConfigRef: MutableRefObject<() => DisplayProfileConfig | null>;
  extractors: DisplayProfileExtractors;
  setCurrentConfig: Dispatch<SetStateAction<ViewerConfiguration | null>>;
  setProfileTheme: Dispatch<SetStateAction<DisplayProfileBackgroundTheme>>;
  setProfileGridDensity: Dispatch<SetStateAction<DisplayProfileGridDensity>>;
  setProfileChannelColors: Dispatch<SetStateAction<Record<string, string>>>;
  setProfileWaveformThickness: Dispatch<SetStateAction<number>>;
  setProfileChannelSpacing: Dispatch<SetStateAction<number>>;
  setProfileLabelFontSize: Dispatch<SetStateAction<number>>;
  setProfileChannelPositionOffsets: Dispatch<SetStateAction<Record<string, number>>>;
  setWaveformDisplayMode: Dispatch<SetStateAction<WaveformDisplayMode>>;
  setAllowTraceOverflow: Dispatch<SetStateAction<boolean>>;
  setRefitOnChannelChange: Dispatch<SetStateAction<boolean>>;
  setSelectedTrendProfileId: Dispatch<SetStateAction<string>>;
  setDualMontagePreference: Dispatch<SetStateAction<DualMontagePreference>>;
  setDockBottomHeightPx: Dispatch<SetStateAction<number>>;
  setDockRightWidthPx: Dispatch<SetStateAction<number>>;
  setAnnotationPanelWidthPx: Dispatch<SetStateAction<number>>;
  setDockBottomCollapsed: Dispatch<SetStateAction<boolean>>;
  setDockRightCollapsed: Dispatch<SetStateAction<boolean>>;
  setDockBottomActiveTab: Dispatch<SetStateAction<DockPanelKey | null>>;
  setDockPanelVisibility: Dispatch<SetStateAction<Record<DockPanelKey, boolean>>>;
  setDockPanelOrder: Dispatch<SetStateAction<DockPanelKey[]>>;
  setCrosshairEnabled: Dispatch<SetStateAction<boolean>>;
  setShowGrid: Dispatch<SetStateAction<boolean>>;
  setShowChannelGroupLabels: Dispatch<SetStateAction<boolean>>;
  setShowScaleMarkers: Dispatch<SetStateAction<boolean>>;
  setHiddenChannels: Dispatch<SetStateAction<Set<string>>>;
  rendererRef: MutableRefObject<RendererTickHandle | null>;
  syncRendererCanvas: () => void;
  mergeNotchFlags: (
    current: Pick<ViewerConfiguration, "notch_filter_60hz" | "notch_filter_50hz">,
    updates: Partial<Pick<ViewerConfiguration, "notch_filter_60hz" | "notch_filter_50hz">>,
  ) => { notch_filter_60hz: boolean; notch_filter_50hz: boolean };
  normalizeViewerConfig: (config: ViewerConfiguration) => ViewerConfiguration;
}) {
  const {
    displayProfilePolicy,
    selectedDisplayProfile,
    currentConfig,
    profileTheme,
    profileGridDensity,
    profileChannelColors,
    profileWaveformThickness,
    profileChannelSpacing,
    profileLabelFontSize,
    profileChannelPositionOffsets,
    waveformDisplayMode,
    allowTraceOverflow,
    refitOnChannelChange,
    selectedTrendProfileId,
    dualMontagePreference,
    dockBottomHeightPx,
    dockRightWidthPx,
    annotationPanelWidthPx,
    dockBottomCollapsed,
    dockRightCollapsed,
    dockBottomActiveTab,
    dockPanelVisibility,
    dockPanelOrder,
    crosshairEnabled,
    showGrid,
    showChannelGroupLabels,
    showScaleMarkers,
    hiddenChannels,
    buildDisplayProfileConfigRef,
    extractors,
    setCurrentConfig,
    setProfileTheme,
    setProfileGridDensity,
    setProfileChannelColors,
    setProfileWaveformThickness,
    setProfileChannelSpacing,
    setProfileLabelFontSize,
    setProfileChannelPositionOffsets,
    setWaveformDisplayMode,
    setAllowTraceOverflow,
    setRefitOnChannelChange,
    setSelectedTrendProfileId,
    setDualMontagePreference,
    setDockBottomHeightPx,
    setDockRightWidthPx,
    setAnnotationPanelWidthPx,
    setDockBottomCollapsed,
    setDockRightCollapsed,
    setDockBottomActiveTab,
    setDockPanelVisibility,
    setDockPanelOrder,
    setCrosshairEnabled,
    setShowGrid,
    setShowChannelGroupLabels,
    setShowScaleMarkers,
    setHiddenChannels,
    rendererRef,
    syncRendererCanvas,
    mergeNotchFlags,
    normalizeViewerConfig,
  } = params;

  const {
    extractChannelColorsFromConfig,
    extractTrendProfileIdFromConfig,
    extractWaveformThicknessFromConfig,
    extractChannelSpacingFromConfig,
    extractDisplayModeFromConfig,
    extractAllowTraceOverflowFromConfig,
    extractRefitOnChannelChangeFromConfig,
    extractLabelFontSizeFromConfig,
    extractChannelPositionOffsetsFromConfig,
    extractDualMontagePreferenceFromConfig,
    extractDockLayoutFromConfig,
    extractDockPanelVisibilityFromConfig,
    extractDockPanelOrderFromConfig,
    extractHiddenChannelsFromConfig,
  } = extractors;

  const applyDisplayProfileToSession = useCallback(
    (profile: EEGDisplayProfile) => {
      const config = (profile.config_json || {}) as DisplayProfileConfig;
      if (!currentConfig) return;
      const notchFlags = mergeNotchFlags(currentConfig, {
        notch_filter_60hz:
          typeof config.notch_filter_60hz === "boolean"
            ? config.notch_filter_60hz
            : undefined,
        notch_filter_50hz:
          typeof config.notch_filter_50hz === "boolean"
            ? config.notch_filter_50hz
            : undefined,
      });

      const nextConfig: ViewerConfiguration = {
        ...currentConfig,
        montage_id:
          typeof config.montage_id === "string"
            ? config.montage_id
            : currentConfig.montage_id,
        sensitivity_value:
          typeof config.sensitivity_value === "number"
            ? config.sensitivity_value
            : currentConfig.sensitivity_value,
        time_scale:
          typeof config.time_scale === "number"
            ? config.time_scale
            : currentConfig.time_scale,
        high_pass_filter:
          typeof config.high_pass_filter === "number"
            ? config.high_pass_filter
            : currentConfig.high_pass_filter,
        low_pass_filter:
          typeof config.low_pass_filter === "number"
            ? config.low_pass_filter
            : currentConfig.low_pass_filter,
        ...notchFlags,
        remove_dc_offset:
          typeof config.remove_dc_offset === "boolean"
            ? config.remove_dc_offset
            : (currentConfig.remove_dc_offset ?? false),
        polarity:
          config.polarity === "positive-up"
            ? "positive-up"
            : config.polarity === "negative-up"
              ? "negative-up"
              : (currentConfig.polarity ?? "negative-up"),
        channel_color_mode:
          config.channel_color_mode === "alternating" ||
          config.channel_color_mode === "by-type" ||
          config.channel_color_mode === "uniform"
            ? config.channel_color_mode
            : (currentConfig.channel_color_mode ?? "uniform"),
        reference_type:
          typeof config.reference_type === "string"
            ? config.reference_type
            : currentConfig.reference_type,
        playback_speed:
          typeof config.playback_speed === "number"
            ? config.playback_speed
            : currentConfig.playback_speed,
      };

      setCurrentConfig(normalizeViewerConfig(nextConfig));

      const theme =
        parseEegPaperTheme(config.background_theme) ??
        displayProfilePolicy.defaults.background_theme;
      const gridDensity =
        typeof config.grid_density === "string" &&
        (config.grid_density === "low" ||
          config.grid_density === "medium" ||
          config.grid_density === "high")
          ? config.grid_density
          : displayProfilePolicy.defaults.grid_density;

      setProfileTheme(theme);
      setProfileGridDensity(gridDensity);
      setProfileChannelColors(extractChannelColorsFromConfig(config));
      setSelectedTrendProfileId(extractTrendProfileIdFromConfig(config));
      setDualMontagePreference(extractDualMontagePreferenceFromConfig(config));
      const dockLayout = extractDockLayoutFromConfig(config);
      setDockBottomHeightPx(dockLayout.bottomHeightPx);
      setDockRightWidthPx(dockLayout.rightWidthPx);
      setAnnotationPanelWidthPx(dockLayout.clinicalWidthPx);
      setDockBottomCollapsed(dockLayout.bottomCollapsed);
      setDockRightCollapsed(dockLayout.rightCollapsed);
      setDockBottomActiveTab(dockLayout.activeBottomTab);
      setDockPanelVisibility(extractDockPanelVisibilityFromConfig(config));
      setDockPanelOrder(extractDockPanelOrderFromConfig(config));
      setCrosshairEnabled(
        typeof config.crosshair_enabled === "boolean"
          ? config.crosshair_enabled
          : true,
      );
      setShowGrid(
        typeof config.show_grid === "boolean" ? config.show_grid : true,
      );
      setShowChannelGroupLabels(
        typeof config.show_channel_group_labels === "boolean"
          ? config.show_channel_group_labels
          : true,
      );
      setShowScaleMarkers(
        typeof config.show_scale_markers === "boolean"
          ? config.show_scale_markers
          : true,
      );
      // EEG-CUX-006: restore hidden channels from profile
      setHiddenChannels(extractHiddenChannelsFromConfig(config));

      const themeConfig = RENDER_THEME_MAP[theme];
      const gridAlpha =
        gridDensity === "low" ? "44" : gridDensity === "high" ? "99" : "66";
      const waveformThickness = extractWaveformThicknessFromConfig(config);
      const channelSpacing = extractChannelSpacingFromConfig(config);
      const displayMode = extractDisplayModeFromConfig(config);
      const nextAllowTraceOverflow =
        extractAllowTraceOverflowFromConfig(config);
      const nextRefitOnChannelChange =
        extractRefitOnChannelChangeFromConfig(config);
      const labelFontSize = extractLabelFontSizeFromConfig(config);
      const channelPositionOffsets =
        extractChannelPositionOffsetsFromConfig(config);

      if (rendererRef.current) {
        syncRendererCanvas();
        rendererRef.current.updateConfig({
          backgroundColor: themeConfig.backgroundColor,
          waveformColor: themeConfig.waveformColor,
          gridColor: withAlphaHex(themeConfig.gridColorBase, gridAlpha),
          gridDensity,
          waveformThickness,
          channelSpacing,
          displayMode,
          allowTraceOverflow: nextAllowTraceOverflow,
          labelFontSize,
          channelPositionOffsets,
        });
      }
      setProfileWaveformThickness(waveformThickness);
      setProfileChannelSpacing(channelSpacing);
      setWaveformDisplayMode(displayMode);
      setAllowTraceOverflow(nextAllowTraceOverflow);
      setRefitOnChannelChange(nextRefitOnChannelChange);
      setProfileLabelFontSize(labelFontSize);
      setProfileChannelPositionOffsets(channelPositionOffsets);
    },
    [
      currentConfig,
      extractChannelColorsFromConfig,
      extractAllowTraceOverflowFromConfig,
      extractRefitOnChannelChangeFromConfig,
      extractDisplayModeFromConfig,
      extractChannelSpacingFromConfig,
      extractChannelPositionOffsetsFromConfig,
      extractDockLayoutFromConfig,
      extractDockPanelOrderFromConfig,
      extractDockPanelVisibilityFromConfig,
      extractDualMontagePreferenceFromConfig,
      extractHiddenChannelsFromConfig,
      extractLabelFontSizeFromConfig,
      extractTrendProfileIdFromConfig,
      extractWaveformThicknessFromConfig,
      displayProfilePolicy.defaults.background_theme,
      displayProfilePolicy.defaults.grid_density,
      profileGridDensity,
      profileTheme,
      syncRendererCanvas,
    ],
  );

    const buildDisplayProfileConfigFromCurrent = useCallback(() => {
    const config = currentConfig;
    if (!config) return null;
    const sanitizedChannelColors: Record<string, string> = {};
    Object.entries(profileChannelColors).forEach(([channelPair, color]) => {
      const pairKey = channelPair.trim();
      const normalizedColor = normalizeHexColor(color);
      if (!pairKey || !normalizedColor) return;
      sanitizedChannelColors[pairKey] = normalizedColor;
    });
    const sanitizedChannelPositionOffsets = sanitizeChannelPositionOffsets(
      profileChannelPositionOffsets,
      displayProfilePolicy.visual.channel_position_offset,
    );

    return {
      montage_id: config.montage_id,
      sensitivity_value: config.sensitivity_value,
      time_scale: config.time_scale,
      high_pass_filter: config.high_pass_filter,
      low_pass_filter: config.low_pass_filter,
      notch_filter_60hz: config.notch_filter_60hz,
      notch_filter_50hz: config.notch_filter_50hz ?? false,
      remove_dc_offset: config.remove_dc_offset ?? false,
      polarity: config.polarity ?? "negative-up",
      channel_color_mode: config.channel_color_mode ?? "uniform",
      reference_type: config.reference_type,
      playback_speed: config.playback_speed,
      display_mode: waveformDisplayMode,
      row_height_mode: getRowHeightModeFromChannelSpacing(
        profileChannelSpacing,
      ),
      allow_trace_overflow: allowTraceOverflow,
      refit_on_channel_change: refitOnChannelChange,
      channel_colors: sanitizedChannelColors,
      waveform_thickness: profileWaveformThickness,
      channel_spacing: profileChannelSpacing,
      label_font_size: profileLabelFontSize,
      channel_position_offsets: sanitizedChannelPositionOffsets,
      grid_density: profileGridDensity,
      background_theme: profileTheme,
      trend_threshold_profile_id: selectedTrendProfileId || null,
      dual_montage_preference: {
        left_montage_id: dualMontagePreference.left_montage_id,
        right_montage_id: dualMontagePreference.right_montage_id,
        sync_cursor: dualMontagePreference.sync_cursor,
        sync_zoom: dualMontagePreference.sync_zoom,
        sync_playback: dualMontagePreference.sync_playback,
        diff_highlight: dualMontagePreference.diff_highlight,
      },
      dock_layout: {
        bottom_height_px: dockBottomHeightPx,
        right_width_px: dockRightWidthPx,
        clinical_width_px: annotationPanelWidthPx,
        bottom_collapsed: dockBottomCollapsed,
        right_collapsed: dockRightCollapsed,
        active_bottom_tab: dockBottomActiveTab ?? undefined,
      },
      panel_visibility: {
        annotation_timeline: dockPanelVisibility.annotation_timeline,
        event_detection: dockPanelVisibility.event_detection,
        trend_correlation: dockPanelVisibility.trend_correlation,
        spectrogram: dockPanelVisibility.spectrogram,
      },
      panel_order: dockPanelOrder,
      crosshair_enabled: crosshairEnabled,
      show_grid: showGrid,
      show_channel_group_labels: showChannelGroupLabels,
      show_scale_markers: showScaleMarkers,
      hidden_channels:
        hiddenChannels.size > 0 ? [...hiddenChannels] : undefined,
    } satisfies DisplayProfileConfig;
  }, [
    allowTraceOverflow,
    refitOnChannelChange,
    crosshairEnabled,
    hiddenChannels,
    currentConfig,
    annotationPanelWidthPx,
    dockBottomActiveTab,
    dockBottomCollapsed,
    dockBottomHeightPx,
    dockPanelOrder,
    dockPanelVisibility.annotation_timeline,
    dockPanelVisibility.event_detection,
    dockPanelVisibility.trend_correlation,
    dockPanelVisibility.spectrogram,
    dockRightCollapsed,
    dockRightWidthPx,
    dualMontagePreference,
    displayProfilePolicy.visual.channel_position_offset,
    waveformDisplayMode,
    profileChannelPositionOffsets,
    profileChannelSpacing,
    profileChannelColors,
    profileLabelFontSize,
    profileGridDensity,
    profileTheme,
    profileWaveformThickness,
    selectedTrendProfileId,
    showGrid,
    showChannelGroupLabels,
    showScaleMarkers,
  ]);

  useEffect(() => {
    buildDisplayProfileConfigRef.current = buildDisplayProfileConfigFromCurrent;
  }, [buildDisplayProfileConfigFromCurrent, buildDisplayProfileConfigRef]);

  useEffect(() => {
    if (!selectedDisplayProfile) {
      setProfileChannelColors({});
      setProfileChannelPositionOffsets({});
      return;
    }
    const selectedConfig =
      selectedDisplayProfile.config_json as DisplayProfileConfig;
    const dockLayout = extractDockLayoutFromConfig(selectedConfig);
    setSelectedTrendProfileId(extractTrendProfileIdFromConfig(selectedConfig));
    setDualMontagePreference(
      extractDualMontagePreferenceFromConfig(selectedConfig),
    );
    setProfileChannelColors(extractChannelColorsFromConfig(selectedConfig));
    setProfileWaveformThickness(
      extractWaveformThicknessFromConfig(selectedConfig),
    );
    setProfileLabelFontSize(extractLabelFontSizeFromConfig(selectedConfig));
    setProfileChannelPositionOffsets(
      extractChannelPositionOffsetsFromConfig(selectedConfig),
    );
    setDockBottomHeightPx(dockLayout.bottomHeightPx);
    setDockRightWidthPx(dockLayout.rightWidthPx);
    setAnnotationPanelWidthPx(dockLayout.clinicalWidthPx);
    setDockBottomCollapsed(dockLayout.bottomCollapsed);
    setDockRightCollapsed(dockLayout.rightCollapsed);
    setDockBottomActiveTab(dockLayout.activeBottomTab);
    setDockPanelVisibility(
      extractDockPanelVisibilityFromConfig(selectedConfig),
    );
    setDockPanelOrder(extractDockPanelOrderFromConfig(selectedConfig));
    // EEG-CUX-006: restore hidden channels from profile
    setHiddenChannels(extractHiddenChannelsFromConfig(selectedConfig));
  }, [
    extractChannelColorsFromConfig,
    extractDockLayoutFromConfig,
    extractDockPanelOrderFromConfig,
    extractDockPanelVisibilityFromConfig,
    extractDualMontagePreferenceFromConfig,
    extractChannelPositionOffsetsFromConfig,
    extractHiddenChannelsFromConfig,
    extractLabelFontSizeFromConfig,
    extractTrendProfileIdFromConfig,
    extractWaveformThicknessFromConfig,
    selectedDisplayProfile,
  ]);

  useEffect(() => {
    if (selectedDisplayProfile) return;
    setProfileWaveformThickness(
      displayProfilePolicy.visual.waveform_thickness.default,
    );
    setWaveformDisplayMode("stacked");
    setProfileLabelFontSize(
      displayProfilePolicy.visual.label_font_size.default,
    );
    setProfileChannelPositionOffsets({});
  }, [
    displayProfilePolicy.visual.label_font_size.default,
    displayProfilePolicy.visual.waveform_thickness.default,
    selectedDisplayProfile,
  ]);

  return {
    applyDisplayProfileToSession,
    buildDisplayProfileConfigFromCurrent,
  };
}
